import React, { useCallback, useEffect, useRef, useState } from 'react';

import IconButton from '@atlaskit/button/icon/button';
import VidFullScreenOff from '@atlaskit/icon/core/minimize';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	useAnalytics,
	useTeamCentralStore,
} from '@atlassian/ptc-common';
import type { useReportingLines } from '@atlassian/teams';

import { useReportingLinesContext } from '../ReportingLinesContext';
import { CardFocusRegistryProvider, type RegisterCard } from './cardFocusRegistry';
import { ReportingLinesEmptyState } from './EmptyState';
import { DirectReportsSection } from './Sections/DirectReportsSection';
import { ManagerSection } from './Sections/ManagerSection';
import { PeersSection } from './Sections/PeersSection';
import { ReportingLinesSkeleton } from './Skeleton';
import * as Styled from './styles';
import { type ReportingLines } from './types';

export const REPORTING_LINES_USER_CARD_SLOTS_LIMIT = 8;

/**
 * The registered card that comes first in the chart, which is also first in the tab order.
 * Registration order follows mount order rather than reading order, so compare positions.
 */
const getFirstCard = (cards: Map<string, HTMLElement>): HTMLElement | undefined =>
	Array.from(cards.values()).reduce<HTMLElement | undefined>(
		(first, card) =>
			first && !(card.compareDocumentPosition(first) & Node.DOCUMENT_POSITION_FOLLOWING)
				? first
				: card,
		undefined,
	);

interface ReportingLinesChartProps {
	reportingLines: ReportingLines;
}

export const ReportingLinesChart = ({
	reportingLines,
}: ReportingLinesChartProps): React.JSX.Element => {
	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();
	const {
		managers: orderedManagerList,
		user: selectedUser,
		reports: directReports,
		peers,
	} = reportingLines;

	const [showAvatarStack, setShowAvatarStack] = useState(true);
	const collapseButtonRef = useRef<HTMLButtonElement>(null);
	const prevShowAvatarStackRef = useRef(true);
	const focusFrameRef = useRef<number | undefined>(undefined);
	// The person an activated overflow stack was hiding first, and the cards currently on
	// screen. Sections know exactly who they hide, so they name the person to focus rather
	// than the chart trying to work it out from the rendered output.
	const focusAfterExpandRef = useRef<string | undefined>(undefined);
	const cardElementsRef = useRef(new Map<string, HTMLElement>());

	const registerCard = useCallback<RegisterCard>((accountIdentifier, element) => {
		if (element) {
			cardElementsRef.current.set(accountIdentifier, element);
		} else {
			cardElementsRef.current.delete(accountIdentifier);
		}
	}, []);

	const [teamCentralStore] = useTeamCentralStore();
	const { getActiveWorkspaceId } = teamCentralStore.teamCentralHandlers;
	const { product = '', viewedEventAttributes } = useReportingLinesContext();
	const workspaceUuid = getActiveWorkspaceId ? getActiveWorkspaceId() : null;

	const toggleAvatarStack = (firstHiddenAccountIdentifier?: string | null) => {
		// Several stacks share one expanded state, so remember who the activated stack was
		// hiding first. That is the person focus should land on once the list re-renders.
		focusAfterExpandRef.current = firstHiddenAccountIdentifier ?? undefined;

		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.ReportingLinesChart.expanded', {
				product,
				workspaceUuid,
			});

			// used to count towards Atlas MAU
			fireEvent('ui.ui.viewed', {
				product,
				workspaceUuid,
			});
		} else {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'ReportingLinesChart',
				action: AnalyticsEventActions.EXPANDED,
				tags: ['watermelon'],
				attributes: { product, workspaceUuid },
			});

			// used to count towards Atlas MAU
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'ui',
				action: AnalyticsEventActions.VIEWED,
				tags: ['watermelon'],
				attributes: { product, workspaceUuid },
			});
		}

		setShowAvatarStack((prev) => !prev);
	};

	useEffect(() => {
		if (!fg('teams_a11y_focus_high_priority')) {
			return;
		}
		// showAvatarStack=true means collapsed (avatar stack visible), false means expanded.
		const wasCollapsed = prevShowAvatarStackRef.current;
		const isCollapsed = showAvatarStack;
		prevShowAvatarStackRef.current = isCollapsed;

		if (wasCollapsed === isCollapsed) {
			return;
		}

		cancelAnimationFrame(focusFrameRef.current ?? 0);
		focusFrameRef.current = requestAnimationFrame(() => {
			if (!isCollapsed) {
				// Just expanded: focus the first person the activated stack was hiding, falling
				// back to the collapse button if that card did not render.
				const firstHidden = focusAfterExpandRef.current;
				const newlyRevealedCard = firstHidden
					? cardElementsRef.current.get(firstHidden)
					: undefined;
				(newlyRevealedCard ?? collapseButtonRef.current)?.focus();
				return;
			}

			// Just collapsed: the collapse button has unmounted, so move focus to the first
			// card of the list rather than letting it fall back to the document body.
			getFirstCard(cardElementsRef.current)?.focus();
		});

		return () => cancelAnimationFrame(focusFrameRef.current ?? 0);
	}, [showAvatarStack]);

	useEffect(() => {
		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('ui.ReportingLinesChart.viewed', {
				product,
				workspaceUuid,
				isNewUserProfile: null,
				tab: null,
				...viewedEventAttributes,
			});
		} else {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'ReportingLinesChart',
				action: AnalyticsEventActions.VIEWED,
				tags: ['watermelon'],
				attributes: { product, workspaceUuid },
			});
		}
	}, [analytics, product, workspaceUuid, fireEvent, viewedEventAttributes]);

	// Extracting these into constants breaks typechecking
	// Checks if any response data is missing (undefined/null) or if no IdP data exists (empty arrays)
	if (
		!selectedUser ||
		!orderedManagerList ||
		!directReports ||
		!peers ||
		(orderedManagerList.length === 0 && directReports.length === 0 && peers.length === 0)
	) {
		return <ReportingLinesEmptyState />;
	}

	const isManager = selectedUser.numberOfDirectReports !== 0;

	return (
		<>
			{!showAvatarStack && (
				<Styled.CollapseButton>
					<IconButton
						ref={collapseButtonRef}
						icon={VidFullScreenOff}
						label="Collapse"
						onClick={() => {
							if (fg('ptc-enable-ptc-common-analytics-refactor')) {
								fireEvent('ui.ReportingLinesChart.collapsed', {
									product,
									workspaceUuid,
								});
								// used to count towards Atlas MAU
								fireEvent('ui.ui.viewed', {
									product,
									workspaceUuid,
								});
							} else {
								analytics.pushUIEvent({
									source: AnalyticsEventSource.USER_PROFILE_SCREEN,
									actionSubject: 'ReportingLinesChart',
									action: AnalyticsEventActions.COLLAPSED,
									tags: ['watermelon'],
									attributes: { product, workspaceUuid },
								});

								// used to count towards Atlas MAU
								analytics.pushUIEvent({
									source: AnalyticsEventSource.USER_PROFILE_SCREEN,
									actionSubject: 'ui',
									action: AnalyticsEventActions.VIEWED,
									tags: ['watermelon'],
									attributes: { product, workspaceUuid },
								});
							}

							setShowAvatarStack(true);
						}}
						testId="reporting-lines-collapse-button"
					/>
				</Styled.CollapseButton>
			)}
			<CardFocusRegistryProvider value={registerCard}>
				<ManagerSection
					orderedManagerList={orderedManagerList}
					showAvatarStack={showAvatarStack}
					toggleAvatarStack={toggleAvatarStack}
					isSelectedUserManager={isManager}
				/>
				{isManager ? (
					<DirectReportsSection
						selectedUser={selectedUser}
						directReports={directReports}
						showAvatarStack={showAvatarStack}
						toggleAvatarStack={toggleAvatarStack}
					/>
				) : (
					<PeersSection
						selectedUser={selectedUser}
						peers={peers}
						showAvatarStack={showAvatarStack}
						toggleAvatarStack={toggleAvatarStack}
					/>
				)}
			</CardFocusRegistryProvider>
		</>
	);
};

interface ReportingLinesChartFromResponseProps {
	reportingLinesResponse: ReturnType<typeof useReportingLines>;
	isResponseEmpty: boolean;
}
export const ReportingLinesChartFromResponse = ({
	reportingLinesResponse: { isLoading, reportingLines },
	isResponseEmpty,
}: ReportingLinesChartFromResponseProps): React.JSX.Element => {
	if (isLoading) {
		return <ReportingLinesSkeleton />;
	}

	if (!isResponseEmpty && !!reportingLines) {
		return <ReportingLinesChart reportingLines={reportingLines} />;
	}
	return <ReportingLinesEmptyState />;
};
